import React from "react";
import { DollarSign } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui";
import { formatCurrency } from "../../../utils/formatters";
import { Vehicle, Benefit, FinancialCharge } from "../../../types";
import { Brand, Model } from "../../../types";

interface TradeVehicleData {
 brandId: string;
 modelId: string;
 condition: string;
 modelYear: number;
 manufactureYear: number;
 mileage: number;
 fuelType: string;
 transmission: string;
 color: string;
 offeredValue: number;
 expenses?: Array<{ name: string; value: number }>;
}

interface PaymentPlan {
 paymentMethodId: string;
 optionId?: string;
 value: number;
 installments?: number;
 date?: string;
}

interface FinancialSummaryProps {
 selectedVehicle: Vehicle | null;
 proposedPrice: number;
 selectedBenefits: string[];
 benefits: Benefit[];
 benefitsWithFullDiscount: string[];
 selectedCharges: string[];
 charges: FinancialCharge[];
 chargeDiscounts: { [key: string]: number };
 savedTradeVehicles: TradeVehicleData[] | null;
 savedPaymentPlans: PaymentPlan[];
 brands: Brand[];
 models: Model[];
 getVehiclePrice: (vehicle: Vehicle) => number;
 getChargeOriginalValue: (charge: FinancialCharge) => number;
 getChargeValue: (charge: FinancialCharge) => number;
 calculateBenefitsTotal: () => number;
 calculateChargesTotal: () => number;
 calculateTradeVehiclesTotal: () => number;
 calculateFinalPrice: () => number;
 calculateTotalPaid: () => number;
 calculateRemainingAmount: () => number;
}

export const FinancialSummary: React.FC<FinancialSummaryProps> = ({
 selectedVehicle,
 proposedPrice,
 selectedBenefits,
 benefits,
 benefitsWithFullDiscount,
 selectedCharges,
 charges,
 chargeDiscounts,
 savedTradeVehicles,
 savedPaymentPlans,
 brands,
 models,
 getVehiclePrice,
 getChargeOriginalValue,
 getChargeValue,
 calculateBenefitsTotal,
 calculateChargesTotal,
 calculateTradeVehiclesTotal,
 calculateFinalPrice,
 calculateTotalPaid,
 calculateRemainingAmount,
}) => {
 if (!selectedVehicle) return null;

 return (
 <div className="w-80 flex-shrink-0">
 <div className="sticky top-6">
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <DollarSign className="h-5 w-5" />
 Resumo Financeiro
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-4">
 {/* Preço Proposto */}
 <div className="space-y-2 pb-2 border-b border-border">
 <div className="flex justify-between items-center text-sm">
 <span className="text-muted-foreground">
 Preço {proposedPrice > 0 ? "Proposto" : "Original"}:
 </span>
 <span className="font-semibold text-foreground">
 {formatCurrency(
 proposedPrice > 0
 ? proposedPrice
 : getVehiclePrice(selectedVehicle)
 )}
 </span>
 </div>
 </div>

 {/* Benefícios */}
 {selectedBenefits.length > 0 && (
 <div className="pt-2 border-t border-border">
 <div className="flex justify-between items-center text-sm mb-2">
 <span className="text-muted-foreground">
 (+) Benefícios:
 </span>
 <span className="font-semibold text-green-600">
 + {formatCurrency(calculateBenefitsTotal())}
 </span>
 </div>
 <div className="space-y-1 text-xs text-muted-foreground pl-2">
 {selectedBenefits.map((benefitId) => {
 const benefit = benefits.find((b) => b.id === benefitId);
 if (!benefit) return null;
 const hasFullDiscount =
 benefitsWithFullDiscount.includes(benefitId);
 return (
 <div key={benefitId} className="flex justify-between">
 <span className="truncate mr-2">{benefit.name}</span>
 <span className="flex-shrink-0">
 {hasFullDiscount ? (
 <span className="text-green-600">
 {formatCurrency(benefit.value)} (100%)
 </span>
 ) : (
 formatCurrency(benefit.value)
 )}
 </span>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Encargos */}
 {selectedCharges.length > 0 && (
 <div className="pt-2 border-t border-border">
 <div className="flex justify-between items-center text-sm mb-2">
 <span className="text-muted-foreground">
 (+) Encargos:
 </span>
 <span className="font-semibold text-red-600">
 + {formatCurrency(calculateChargesTotal())}
 </span>
 </div>
 <div className="space-y-1 text-xs text-muted-foreground pl-2">
 {selectedCharges.map((chargeId) => {
 const charge = charges.find((c) => c.id === chargeId);
 if (!charge) return null;
 const discount = chargeDiscounts[chargeId] || 0;
 const originalValue = getChargeOriginalValue(charge);
 const finalValue = getChargeValue(charge);
 return (
 <div key={chargeId} className="flex justify-between">
 <span className="truncate mr-2">{charge.name}</span>
 <span className="flex-shrink-0">
 {discount > 0 ? (
 <>
 <span className="line-through text-muted-foreground mr-1">
 {formatCurrency(originalValue)}
 </span>
 <span className="text-green-600">
 {formatCurrency(finalValue)}
 </span>
 <span className="text-green-600 ml-1 text-[10px]">
 (-{discount.toFixed(1)}%)
 </span>
 </>
 ) : (
 formatCurrency(originalValue)
 )}
 </span>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Veículos de Troca */}
 {savedTradeVehicles &&
 savedTradeVehicles.length > 0 &&
 calculateTradeVehiclesTotal() > 0 && (
 <div className="pt-2 border-t border-border">
 <div className="flex justify-between items-center text-sm mb-2">
 <span className="text-muted-foreground">
 (-) Veículos de Troca:
 </span>
 <span className="font-semibold text-blue-600 dark:text-blue-400">
 - {formatCurrency(calculateTradeVehiclesTotal())}
 </span>
 </div>
 <div className="space-y-1 text-xs text-muted-foreground pl-2">
 {savedTradeVehicles.map((vehicle, index) => {
 const brand = brands.find(
 (b) => b.id === vehicle.brandId
 );
 const model = models.find(
 (m) => m.id === vehicle.modelId
 );
 const vehicleName =
 brand && model && vehicle.modelYear
 ? `${brand.name} ${model.name} ${vehicle.modelYear}`
 : brand && model
 ? `${brand.name} ${model.name}`
 : `Veículo ${index + 1}`;
 const offeredValue = vehicle.offeredValue || 0;
 const expensesTotal =
 vehicle.expenses?.reduce(
 (sum, e) => sum + e.value,
 0
 ) || 0;
 const totalValue = offeredValue - expensesTotal;
 return (
 <div key={index} className="flex justify-between">
 <span className="truncate mr-2">{vehicleName}</span>
 <span className="flex-shrink-0">
 {expensesTotal > 0 ? (
 <>
 <span className="line-through text-muted-foreground mr-1">
 {formatCurrency(offeredValue)}
 </span>
 <span>{formatCurrency(totalValue)}</span>
 </>
 ) : (
 formatCurrency(totalValue)
 )}
 </span>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Valor Final */}
 <div className="pt-3 border-t-2 border-border">
 <div className="flex justify-between items-center">
 <span className="text-base font-semibold text-foreground">
 Valor Final:
 </span>
 <span className="text-xl font-bold text-accent">
 {formatCurrency(calculateFinalPrice())}
 </span>
 </div>
 </div>

 {/* Pagamentos */}
 {savedPaymentPlans.length > 0 && (
 <div className="pt-2 border-t border-border">
 <div className="flex justify-between items-center text-sm mb-2">
 <span className="text-muted-foreground">
 Total Pago:
 </span>
 <span className="font-semibold text-green-600">
 {formatCurrency(calculateTotalPaid())}
 </span>
 </div>
 </div>
 )}

 {/* Falta Pagar */}
 {calculateRemainingAmount() > 0 && (
 <div className="pt-2 border-t border-border">
 <div className="flex justify-between items-center text-sm">
 <span className="text-muted-foreground">
 Falta Pagar:
 </span>
 <span
 className={`font-semibold ${
 calculateRemainingAmount() > 0
 ? "text-red-600"
 : "text-green-600"
 }`}
 >
 {formatCurrency(calculateRemainingAmount())}
 </span>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 );
};
